import { useState, type FormEvent } from 'react';
import { api, type SkillPack, type SkillPackInput } from '../api';
import { useAction, useLoader } from '../hooks';
import { Alert } from './Alert';
import { ConfirmDialog } from './ConfirmDialog';
import { Field } from './Field';
import { Tag } from './Tag';

/** Même forme que le manifeste et l'agent : un dossier de `~/.dsh/skills/`. */
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,40}$/;
const REF_RE = /^[A-Za-z0-9][A-Za-z0-9._/-]{0,99}$/;
/** Espace de noms réservé : `~/.dsh/skills/wh/` est réécrit à chaque démarrage de l'instance. */
const RESERVED_NAMESPACES = ['wh'];

function isHttps(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === 'https:' && !u.username && !u.password;
  } catch {
    return false;
  }
}

function isSafePath(value: string): boolean {
  if (value === '') return true;
  return (
    /^[A-Za-z0-9._/-]+$/.test(value) &&
    value.split('/').every((seg) => seg !== '' && seg !== '.' && seg !== '..' && !seg.startsWith('-'))
  );
}

function PackForm({
  pack,
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  pack?: SkillPack;
  busy: boolean;
  error: string | null;
  onSubmit: (input: SkillPackInput) => void;
  onCancel?: () => void;
}) {
  const [name, setName] = useState(pack?.name ?? '');
  const [namespace, setNamespace] = useState(pack?.namespace ?? '');
  const [repo, setRepo] = useState(pack?.repo ?? '');
  const [ref, setRef] = useState(pack?.ref ?? 'main');
  const [path, setPath] = useState(pack?.path ?? '');
  const [locked, setLocked] = useState(pack?.locked ?? false);
  const [mandatory, setMandatory] = useState(pack?.mandatory ?? false);
  const [description, setDescription] = useState(pack?.description ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  function submit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!pack && !SLUG_RE.test(name.trim()))
      next.name = 'Minuscules, chiffres et tirets (41 caractères au plus).';
    const ns = namespace.trim();
    if (!SLUG_RE.test(ns)) next.namespace = 'Minuscules, chiffres et tirets (41 caractères au plus).';
    else if (RESERVED_NAMESPACES.includes(ns))
      next.namespace = `« ${ns} » est réservé : il est réécrit à chaque démarrage.`;
    if (!isHttps(repo.trim())) next.repo = 'Dépôt Git en https:// (sans identifiants dans l’URL).';
    if (!REF_RE.test(ref.trim()) || ref.includes('..'))
      next.ref = 'Référence Git invalide (branche, tag ou commit).';
    if (!isSafePath(path.trim()))
      next.path = 'Chemin relatif sans « .. », sans segment vide ni commençant par « - ».';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    const cleanPath = path.trim();
    onSubmit({
      name: name.trim(),
      namespace: ns,
      repo: repo.trim(),
      ref: ref.trim(),
      // En édition, un chemin vidé est envoyé vide (`''`) pour l'effacer ; en création, absent.
      ...(cleanPath ? { path: cleanPath } : pack?.path ? { path: '' } : {}),
      locked,
      mandatory,
      description: description.trim(),
    });
  }

  return (
    <form className="form-grid" onSubmit={submit} noValidate>
      {!pack && (
        <Field label="Nom du pack" error={errors.name}>
          {(p) => (
            <input
              {...p}
              type="text"
              value={name}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
      )}
      <Field
        label="Espace de noms"
        hint={`Dossier ~/.dsh/skills/${namespace.trim() || '<espace>'}/ de l'instance (jamais « wh »).`}
        error={errors.namespace}
      >
        {(p) => (
          <input
            {...p}
            type="text"
            value={namespace}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            onChange={(e) => setNamespace(e.target.value)}
          />
        )}
      </Field>
      <Field label="Dépôt Git (https)" error={errors.repo}>
        {(p) => (
          <input
            {...p}
            type="text"
            value={repo}
            placeholder="https://gitlab.weaverize.com/…/skills.git"
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setRepo(e.target.value)}
          />
        )}
      </Field>
      <Field
        label="Référence (ref)"
        hint="Branche, tag ou commit. Un commit ou un pack verrouillé n'est relu qu'au changement de ref."
        error={errors.ref}
      >
        {(p) => (
          <input
            {...p}
            type="text"
            value={ref}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setRef(e.target.value)}
          />
        )}
      </Field>
      <Field
        label="Sous-dossier (path)"
        hint="Facultatif : dossier du dépôt qui contient les <skill>/SKILL.md."
        error={errors.path}
      >
        {(p) => (
          <input
            {...p}
            type="text"
            value={path}
            placeholder="skills"
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setPath(e.target.value)}
          />
        )}
      </Field>
      <Field label="Description">
        {(p) => (
          <textarea {...p} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        )}
      </Field>
      <div className="field">
        <label className="check">
          <input type="checkbox" checked={locked} onChange={(e) => setLocked(e.target.checked)} />
          Verrouillé (l'instance ne relit pas la ref tant qu'elle ne change pas)
        </label>
        <label className="check">
          <input type="checkbox" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} />
          Obligatoire (inclus pour toute instance)
        </label>
      </div>
      {error && <Alert>{error}</Alert>}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {pack ? 'Enregistrer' : 'Ajouter le pack'}
        </button>
        {onCancel && (
          <button type="button" className="btn" onClick={onCancel}>
            Annuler
          </button>
        )}
      </div>
    </form>
  );
}

export function SkillsTab() {
  const { data: packs, error, reload } = useLoader(api.skillPacks);
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [removing, setRemoving] = useState<SkillPack | null>(null);
  const [typed, setTyped] = useState('');
  const create = useAction();
  const edit = useAction();
  const del = useAction();

  async function add(input: SkillPackInput) {
    if ((await create.run(() => api.createSkillPack(input))) !== undefined) {
      setCreating(false);
      await reload();
    }
  }

  async function save(pack: SkillPack, input: SkillPackInput) {
    const { name: _name, ...patch } = input;
    if ((await edit.run(() => api.updateSkillPack(pack.id, patch))) !== undefined) {
      setEditId(null);
      await reload();
    }
  }

  async function remove(pack: SkillPack) {
    const ok = await del.run(async () => {
      await api.deleteSkillPack(pack.id);
      return true;
    });
    if (ok) await reload();
    setRemoving(null);
    setTyped('');
  }

  return (
    <div className="stack">
      <div className="page-head">
        <p className="muted">
          Packs de skills issus de dépôts Git, synchronisés par l'agent dans{' '}
          <code>~/.dsh/skills/&lt;espace&gt;/</code>. Un pack obligatoire est servi à toutes les instances ;
          les autres s'attribuent instance par instance.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => setCreating((v) => !v)}>
          {creating ? 'Annuler' : 'Nouveau pack'}
        </button>
      </div>

      {creating && (
        <section className="panel" aria-label="Nouveau pack de skills">
          <h2>Nouveau pack de skills</h2>
          <PackForm busy={create.busy} error={create.error} onSubmit={(i) => void add(i)} />
        </section>
      )}

      {del.error && <Alert>{del.error}</Alert>}
      {error && <Alert>{error}</Alert>}
      {packs === null && !error && (
        <p className="muted" aria-busy="true">
          Chargement des packs…
        </p>
      )}
      {packs?.length === 0 && (
        <div className="empty">
          <p>Aucun pack de skills.</p>
          <p className="muted">Ajoutez un dépôt Git de skills pour le diffuser aux instances.</p>
        </div>
      )}
      {packs && packs.length > 0 && (
        <div className="table-wrap">
          <table>
            <caption className="sr-only">Packs de skills</caption>
            <thead>
              <tr>
                <th scope="col">Pack</th>
                <th scope="col">Dépôt</th>
                <th scope="col">Ref · chemin</th>
                <th scope="col">Options</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {packs.map((p) => (
                <tr key={p.id} className={p.id === editId ? 'row-selected' : undefined}>
                  <th scope="row" className="user-cell">
                    <div>{p.name}</div>
                    <div className="muted">
                      <code>{p.namespace}</code>
                    </div>
                  </th>
                  <td className="url">{p.repo}</td>
                  <td>
                    <code>{p.ref}</code>
                    {p.path && (
                      <>
                        {' · '}
                        <code>{p.path}</code>
                      </>
                    )}
                  </td>
                  <td>
                    <div className="row-actions">
                      {p.mandatory && <Tag tone="orange">Obligatoire</Tag>}
                      {p.locked && <Tag tone="gray">Verrouillé</Tag>}
                      {!p.mandatory && !p.locked && <span className="muted">—</span>}
                    </div>
                  </td>
                  <td>
                    <div className="row-actions">
                      <button
                        type="button"
                        className="btn btn-small"
                        aria-pressed={p.id === editId}
                        onClick={() => setEditId(p.id === editId ? null : p.id)}
                      >
                        Modifier <span className="sr-only">le pack {p.name}</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-small btn-danger-outline"
                        onClick={() => {
                          setTyped('');
                          setRemoving(p);
                        }}
                      >
                        Supprimer <span className="sr-only">le pack {p.name}</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {packs
        ?.filter((p) => p.id === editId)
        .map((p) => (
          <section key={p.id} className="panel" aria-label={`Modifier le pack ${p.name}`}>
            <h2>Modifier le pack « {p.name} »</h2>
            <PackForm
              pack={p}
              busy={edit.busy}
              error={edit.error}
              onSubmit={(i) => void save(p, i)}
              onCancel={() => setEditId(null)}
            />
          </section>
        ))}

      {removing && (
        <ConfirmDialog
          title="Supprimer le pack"
          confirmLabel="Supprimer le pack"
          danger
          busy={del.busy}
          confirmDisabled={typed !== removing.name}
          onConfirm={() => void remove(removing)}
          onCancel={() => setRemoving(null)}
        >
          <p>
            Le pack <strong>{removing.name}</strong> sera retiré du catalogue ; l'agent supprimera son dossier
            <code> ~/.dsh/skills/{removing.namespace}/</code> des instances concernées.
          </p>
          <Field label={`Retapez ${removing.name} pour confirmer`}>
            {(p) => (
              <input
                {...p}
                type="text"
                value={typed}
                autoFocus
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setTyped(e.target.value)}
              />
            )}
          </Field>
        </ConfirmDialog>
      )}
    </div>
  );
}
